import { useState, type JSX } from "react";
import { createFileRoute, useRouteContext, useRouter } from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import CheckCircle from "@repo/icons/default/CheckCircle";
import Left from "@repo/icons/default/Left";

import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { VehicleForm, type VehicleFormValues } from "@/components/vehicles/vehicle-form";
import {
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";

const InsertVehicleMutation = graphql(/* GraphQL */ `
  mutation InsertVehicleMutation($object: usuariosVeiculos_insert_input!) {
    insert_usuariosVeiculos_one(object: $object) {
      codUsuarioVeiculo
    }
  }
`);

const VehiclesPageQueryDocument = graphql(/* GraphQL */ `
  query VehiclesPageQueryDocument {
    usuariosVeiculos(where: { dataDesativacao: { _is_null: true } }) {
      codUsuarioVeiculo
    }
  }
`);

const VehicleCreatePage = (): JSX.Element => {
  const { user } = useRouteContext({ from: "__root__" });
  const router = useRouter();
  const [showSuccessDrawer, setShowSuccessDrawer] = useState(false);

  const invalidateVehiclesQuery = useInvalidateQuery(VehiclesPageQueryDocument);

  const { mutateAsync } = useGraphQLMutationWithErrorHandler(InsertVehicleMutation);

  const handleSubmit = async (data: VehicleFormValues): Promise<void> => {
    await mutateAsync(
      {
        object: {
          codUsuario: user.codUsuario,
          marca: data.marca,
          modelo: data.modelo,
          cor: data.cor,
          placa: data.placa,
        },
      },
      {
        onSuccess: () => {
          invalidateVehiclesQuery();
          setShowSuccessDrawer(true);
        },
      },
    );
  };

  const handleCloseSuccessDrawer = (): void => {
    setShowSuccessDrawer(false);
    router.history.back();
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Veículos" align="center" />
      </HeaderRoot>
      <Page>
        <VehicleForm onSubmit={handleSubmit} submitLabel="Salvar" />
      </Page>

      <MessageDrawerRoot
        icon={CheckCircle}
        dismissible
        setOpen={setShowSuccessDrawer}
        open={showSuccessDrawer}
        variant="secondary"
      >
        <MessageDrawerTitle>Veículo cadastrado com sucesso!</MessageDrawerTitle>
        <MessageDrawerActions>
          <MessageDrawerActionButton onClick={handleCloseSuccessDrawer}>
            Ok, entendi
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>
    </>
  );
};

export const Route = createFileRoute("/settings/profile/vehicles/create")({
  component: VehicleCreatePage,
});
